import {DocumentNode, useLazyQuery, useMutation, useQuery} from '@apollo/client';
import {CursorBasedPaginationResponse, FilterType} from 'api/common';
import {workspaceMutations, workspaceQueries} from 'api/graphql-queries';
import {ResponseViewModel} from 'models';
import {UserListingResponseModel} from 'models/tenant.model';
import {AllUsersForWorkspaceRequest, UserIdsForWorkspaceRequest, WorkspaceGetAllResponse} from 'models/workspace.model';

export const useGetAllWorkSpaces = (query: DocumentNode = workspaceQueries.GET_WORKSPACES) => {
    const [refetch, {loading, data}] = useLazyQuery<{workspaces: WorkspaceGetAllResponse[]}>(query, {
        fetchPolicy: 'no-cache', // Doesn't check cache before making a network request
    });

    return {
        getAllWorkSpacesResponse: data?.workspaces ?? [],
        getAllWorkSpacesLoading: loading,
        getAllWorkSpaces: refetch,
    };
};

export const useGetWorkspaceById = (id: string) => {
    const {data, refetch, loading, error} = useQuery(workspaceQueries.GET_WORKSPACE_BY_ID as DocumentNode, {
        variables: {id: id},
        skip: !!!id,
    });
    return {workspace: data, refetch, loading, error};
};

export const useAddUsersToWorkspace = () => {
    const [addUsersToWorkspace, {data, loading}] = useMutation<
        {addUsersToWorkspace: ResponseViewModel<string>},
        {request: UserIdsForWorkspaceRequest}
    >(workspaceMutations.ADD_USERS_TO_WORKSPACE, {
        fetchPolicy: 'no-cache',
    });
    return {
        addUsersToWorkspace,
        addUsersToWorkspaceResponse: data?.addUsersToWorkspace,
        addUsersToWorkspaceLoading: loading,
    };
};

export const useRemoveUsersFromWorkspace = () => {
    const [removeUsersFromWorkspace, {data, loading}] = useMutation<
        {removeUsersFromWorkspace: ResponseViewModel<string>},
        {request: UserIdsForWorkspaceRequest}
    >(workspaceMutations.REMOVE_USERS_FROM_WORKSPACE, {
        fetchPolicy: 'no-cache',
    });
    return {
        removeUsersFromWorkspace,
        removeUsersFromWorkspaceResponse: data?.removeUsersFromWorkspace,
        removeUsersFromWorkspaceLoading: loading,
    };
};

export const useGetUsersByWorkspaceId = () => {
    const [usersByWorkspaceId, {data, loading}] = useLazyQuery<
        {usersByWorkspaceSlug: CursorBasedPaginationResponse<UserListingResponseModel>},
        AllUsersForWorkspaceRequest
    >(workspaceQueries.GET_USERS_BY_WORKSPACE_SLUG, {
        fetchPolicy: 'no-cache',
    });

    return {
        usersByWorkspaceId,
        usersByWorkspaceIdResponse: data?.usersByWorkspaceSlug,
        usersByWorkspaceIdLoading: loading,
    };
};

export const useGetUsersOfAllWorkspacesByUserId = () => {
    const [getUsersOfAllWorkspacesByUserId, {data, loading}] = useLazyQuery<
        {usersOfAllWorkspacesByUserId: UserListingResponseModel[]},
        {tenantId: string; where?: FilterType<UserListingResponseModel>}
    >(workspaceQueries.GET_USERS_OF_ALL_WORKSPACES_BY_USER_ID, {
        fetchPolicy: 'no-cache',
    });
    return {
        getUsersOfAllWorkspacesByUserId,
        getUsersOfAllWorkspacesByUserIdResponse: data?.usersOfAllWorkspacesByUserId,
        getUsersOfAllWorkspacesByUserIdLoading: loading,
    };
};

export const useUpdateUserWorkspaceToBeSelected = () => {
    const [updateUserWorkspaceToBeSelected, {data, loading}] = useMutation<
        {updateUserWorkspaceToBeSelected: ResponseViewModel<string>},
        {workspaceId: string}
    >(workspaceMutations.UPDATE_USER_WORKSPACE_TO_BE_SELECTED, {
        fetchPolicy: 'no-cache',
    });
    return {
        updateUserWorkspaceToBeSelected,
        updateUserWorkspaceToBeSelectedResponse: data?.updateUserWorkspaceToBeSelected,
        updateUserWorkspaceToBeSelectedLoading: loading,
    };
};
